import { useState } from "react";
import usePut from "../../../../hooks/usePut";
import useRead from "../../../../hooks/useRead";
import toast from "react-hot-toast";

export const useProofOfInvestmentsApi = () => {
  const [attachments, setAttachments] = useState({});
  const [activeAttachmentData, setActiveAttachmentData] = useState(null);
  const [openPopoverKey, setOpenPopoverKey] = useState(null);
  const [activeCommentData, setActiveCommentData] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [isCommentInputFocused, setIsCommentInputFocused] = useState(false);
  const [currentProofListParams, setCurrentProofListParams] = useState(null);
  const [currentCommentListParams, setCurrentCommentListParams] = useState(null);
  const [commentPage, setCommentPage] = useState(0);
  const [hasMoreComments, setHasMoreComments] = useState(false);

  // API hooks for document management
  const uploadDocument = usePut({
    url: "uploadDocument",
    onSuccess: (response) => {
      console.log("Document uploaded successfully:", response);
      if (response?.data?.data?.[0]?.image_id) {
        const imageId = response.data.data[0].image_id;
        handleAttachProof(imageId);
      }
    },
    onError: (error) => {
      toast.error(error.response.data?.message || "Document upload failed");
      setActiveAttachmentData(null);
    },
  });

  const attachProof = usePut({
    url: "tds/document-proof/upload",
    refreshUrl: ["tds/document-proof/list", "tds/get-user-response"],
    onSuccess: (response) => {
      toast.success("Proof attached successfully");
      setActiveAttachmentData(null);
    },
    onError: (error) => {
      toast.error(error.response.data?.message || "Failed to attach proof");
      setActiveAttachmentData(null);
    },
  });

  const deleteProof = usePut({
    url: "tds/document-proof/delete",
    refreshUrl: ["tds/document-proof/list", "tds/get-user-response"],
    onSuccess: (response) => {
      console.log("Proof deleted successfully:", response);
      toast.success("Document deleted successfully");
      if (currentProofListParams) {
        setProofListParams({ ...currentProofListParams });
      }
    },
    onError: (error) => {
      console.error("Proof deletion failed:", error);
      toast.error("Failed to delete document");
    },
  });

  const addComment = usePut({
    url: "tds/comment/add",
    refreshUrl: ["tds/comment/list", "tds/get-user-response"],
    onSuccess: (response) => {
      console.log("Comment added successfully:", response);
      toast.success("Comment added successfully");
      setCommentText("");
      setIsCommentInputFocused(false);
      setCommentPage(0);
      if (currentCommentListParams) {
        const refreshParams = { 
          ...currentCommentListParams, 
          page_number: 0
        };
        setCurrentCommentListParams(refreshParams);
        setCommentListParams(refreshParams);
      }
    },
    onError: (error) => {
      console.error("Comment addition failed:", error);
      toast.error("Failed to add comment");
    },
  });

  const { list: proofList, setGetListParams: setProofListParams } = useRead({
    url: "tds/document-proof/list",
    initialData: {},
    method: "GET",
    enabled: !!currentProofListParams,
    onSuccess: (data) => {
      if (data?.data) {
        const formattedProofs = data.data.map(proof => ({
          id: proof.investment_proof_id,
          name: `Document ${proof.investment_proof_id}`,
          url: proof.media_type,
        }));
        
        setAttachments(prev => ({
          ...prev,
          [openPopoverKey]: formattedProofs
        }));
      }
    },
  });

  const { list: commentList, setGetListParams: setCommentListParams } = useRead({
    url: "tds/comment/list",
    initialData: {},
    method: "GET",
    enabled: !!currentCommentListParams,
    onSuccess: (data) => {
      console.log("Comments loaded:", data);
      if (data?.data) {
        const formattedComments = data.data.map(comment => {
          // simplified name extraction with clear defaults
          const rawFull = (comment.name || `${comment.first_name || ''} ${comment.last_name || ''}`).trim();
          const fullName = rawFull || "--";
          const firstName = comment.first_name || fullName.split(' ')[0] || "--";
          const initials = fullName === "--" ? "--" : fullName.charAt(0).toUpperCase();

          return {
            id: comment.tds_comment_id,
            text: comment.comment_text,
            author: fullName,
            firstName,
            initials,
            timestamp: new Date(comment.created_at).toLocaleString('en-IN', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: true
            }),
          };
        });
        
        if (commentPage === 0) {
          setComments(formattedComments);
        } else {
          setComments(prev => [...prev, ...formattedComments]);
        }
        
        const totalCount = data.total_count || 0;
        const currentCount = (commentPage + 1) * 10;
        setHasMoreComments(currentCount < totalCount);
      }
    },
  });

  const handleAttachProof = (imageId) => {
    console.log("handleAttachProof called with imageId:", imageId, "activeAttachmentData:", activeAttachmentData);
    
    if (!activeAttachmentData) {
      console.error("No activeAttachmentData found!");
      return;
    }
    
    const payload = {
      document_id: imageId,
      ...activeAttachmentData.idParams
    };
    
    console.log("Attaching proof with payload:", payload);
    attachProof.handleAuth.mutate(payload);
  };

  return {
    // State
    attachments,
    setAttachments,
    activeAttachmentData,
    setActiveAttachmentData,
    openPopoverKey,
    setOpenPopoverKey,
    activeCommentData,
    setActiveCommentData,
    comments,
    setComments,
    commentText,
    setCommentText,
    isCommentInputFocused,
    setIsCommentInputFocused,
    currentProofListParams,
    setCurrentProofListParams,
    currentCommentListParams,
    setCurrentCommentListParams,
    commentPage,
    setCommentPage,
    hasMoreComments,
    setHasMoreComments,

    // API mutations
    uploadDocument,
    attachProof,
    deleteProof,
    addComment,

    // API queries
    proofList,
    setProofListParams,
    commentList,
    setCommentListParams,

    // Handlers
    handleAttachProof,
  };
};
